// y04 Kvartal — corporate-anniversary "Quarter" catalog card.
//
// An editorial corporate-anniversary preview presented as a printed invite:
// a soft gold mat outside an ink-on-paper inner card, with the medallion
// rendered as a stamped roundel ("10" milestone). Cormorant Garamond holds
// the names; the small-caps caption sits at the bottom.
import { Photo, type LayoutCtx } from "./_shared";

export function CorporateQuarterlyPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Subtle warm-gold foil — a Madison-Avenue stationer's foil edge.
  const foil = `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 60%, #fff 40%) 45%, ${accent} 100%)`;

  // Pull the milestone out of the names (e.g. "Sharq Texnologiya · 10" → "10").
  const dotMatch = names.split(" · ");
  const display = dotMatch[0] ?? names;
  const milestone = dotMatch.length > 1 ? dotMatch[dotMatch.length - 1] : "";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: foil,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          YUBILEY
        </div>

        {/* Stamped milestone roundel — embossed gold. */}
        <div
          style={{
            width: "58%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle at 50% 45%, ${bg} 0%, ${accent}10 60%, ${bg})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `inset 0 0 0 1px ${accent}66, 0 0 0 2px ${bg}, 0 0 0 3px ${accent}33`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "90%", height: "90%" }} aria-hidden>
                {/* Two concentric hairlines */}
                <circle cx="50" cy="50" r="42" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.55" />
                <circle cx="50" cy="50" r="36" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.45" />
                {/* Laurel half-wreaths */}
                {[-1, 1].map((side) => (
                  <g key={side} transform={`translate(50 50) scale(${side} 1)`}>
                    {[0, 1, 2, 3, 4].map((i) => {
                      const a = -65 + i * 18;
                      return (
                        <g key={i} transform={`rotate(${a}) translate(0 -32)`}>
                          <ellipse cx="0" cy="0" rx="4" ry="1.6" fill={accent} opacity="0.7" />
                        </g>
                      );
                    })}
                  </g>
                ))}
                {/* Milestone number */}
                <text
                  x="50"
                  y="60"
                  textAnchor="middle"
                  fontFamily="serif"
                  fontWeight="600"
                  fontSize="28"
                  fill={accent}
                >
                  {milestone || "10"}
                </text>
                <text
                  x="50"
                  y="74"
                  textAnchor="middle"
                  fontFamily="sans-serif"
                  fontSize="5"
                  letterSpacing="2"
                  fill={accent}
                  opacity="0.7"
                >
                  YIL
                </text>
              </svg>
            }
          />
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
          }}
        >
          {display}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          YUBILEY · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
